/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { TestPaneSectionLabel } from 'src/luis/projects/testPane/components/common/TestPaneSectionLabel';
import { UtteranceSentiment } from 'src/luis/projects/utterances/types/Utterance';

export type SentimentInspectorProps = {
    /**
     * The sentiment analysis service results per utterance
     */
    sentiment: UtteranceSentiment;
};

const messages = defineMessages({
    sentiment: {
        id: 'testPane.sentimentInspector.sentiment',
        defaultMessage: 'Sentiment'
    },
    sentimentNotEnabled: {
        id: 'testPane.sentimentInspector.sentimentNotEnabled',
        defaultMessage: 'Enable sentiment analysis to get sentiment score'
    }
});

/**
 * Represents the sentiment segment in the utterance inspector.
 *
 * @param props The input properties for the inspector.
 */
export const SentimentInspector = (props: SentimentInspectorProps) => {
    const { sentiment } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <TestPaneSectionLabel>{localize(messages.sentiment)}</TestPaneSectionLabel>

            {sentiment ? (
                <LuisText data-automation-id="sentiment">
                    {sentiment.label} ({sentiment.score})
                </LuisText>
            ) : (
                <LuisText data-automation-id="emptyMessage">{localize(messages.sentimentNotEnabled)}</LuisText>
            )}
        </Stack>
    );
};
